import React from 'react';
import { useThemeColors } from '@/styles';
import { typography } from '@/styles/design-tokens';
import type { ColorPalette } from '@/styles/colors/types';
import type { ProgressDay } from '@/types/learning-progress.types';
import { APP_TIMEZONE } from '@/config/environment';

/** Heatmap visualization constants (below design-token scale) */
const CELL_GAP = '3px';
const CELL_RADIUS = 3;
const LEGEND_DOT_RADIUS = 2;

interface MyProgressProps {
  data: ProgressDay[];
  weeks: number;
  colors?: ColorPalette;
}

export function MyProgress({
  data,
  weeks,
  colors: colorsProp,
}: MyProgressProps) {
  const themeColors = useThemeColors();
  const colors = colorsProp ?? themeColors;

  // Build a map for quick lookup
  const countMap = new Map<string, number>();
  let maxCount = 0;
  for (const day of data) {
    countMap.set(day.date, day.count);
    if (day.count > maxCount) maxCount = day.count;
  }

  // Generate grid: weeks x 7 days (Sun–Sat rows, week columns)
  // Use noon-UTC anchors: noon UTC = 17:30 IST (same calendar day), so UTC date === IST date.
  // This avoids toISOString() returning the previous UTC date for IST midnight.
  const toISTDateStr = (d: Date): string =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: APP_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);

  const noonUTC = (dateStr: string) => new Date(`${dateStr}T12:00:00Z`);

  const todayStr = toISTDateStr(new Date());
  const todayNoon = noonUTC(todayStr);
  const currentDayOfWeek = todayNoon.getUTCDay(); // UTC day-of-week at noon = IST day-of-week

  const startNoon = new Date(todayNoon);
  startNoon.setUTCDate(
    todayNoon.getUTCDate() - currentDayOfWeek - (weeks - 1) * 7
  );

  const totalDays = weeks * 7;
  const days: { date: string; count: number }[] = [];

  for (let i = 0; i < totalDays; i++) {
    const d = new Date(startNoon);
    d.setUTCDate(d.getUTCDate() + i);
    const dateStr = toISTDateStr(d);
    days.push({ date: dateStr, count: countMap.get(dateStr) || 0 });
  }

  // The last column is always the current week
  const currentWeekIdx = weeks - 1;

  // Compute month labels — one per week column, only show on first week of that month
  const monthHeaders: (string | null)[] = [];
  let prevMonth = '';
  for (let w = 0; w < weeks; w++) {
    const firstDayOfWeek = days[w * 7];
    const monthStr = new Intl.DateTimeFormat('en-US', {
      timeZone: APP_TIMEZONE,
      month: 'short',
    }).format(noonUTC(firstDayOfWeek.date));
    if (monthStr !== prevMonth) {
      monthHeaders.push(monthStr);
      prevMonth = monthStr;
    } else {
      monthHeaders.push(null);
    }
  }

  const getIntensity = (count: number): number => {
    if (count === 0 || maxCount === 0) return 0;
    return Math.ceil((count / maxCount) * 4);
  };

  const getCellColor = (intensity: number): string => {
    switch (intensity) {
      case 0:
        return colors.background;
      case 1:
        return `color-mix(in srgb, ${colors.success} 25%, ${colors.background})`;
      case 2:
        return `color-mix(in srgb, ${colors.success} 50%, ${colors.background})`;
      case 3:
        return `color-mix(in srgb, ${colors.success} 75%, ${colors.background})`;
      case 4:
        return colors.success;
      default:
        return colors.background;
    }
  };

  // Sun–Sat order
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // CSS grid: 1 label column + N week columns
  const gridTemplateColumns = `2rem repeat(${weeks}, 1fr)`;
  const cellGap = CELL_GAP;

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns,
          gap: cellGap,
        }}
      >
        {/* Row 0: week headers — only label the current week */}
        <div />
        {Array.from({ length: weeks }, (_, i) => (
          <div
            key={i}
            className="text-[10px] sm:text-xs"
            style={{
              color: colors.text.primary,
              fontWeight: 600,
              lineHeight: typography.lineHeights.tight,
              paddingBottom: 2,
            }}
          >
            {i === currentWeekIdx ? 'This week' : ''}
          </div>
        ))}

        {/* Rows 1–7: day label + cells */}
        {dayLabels.map((label, dayIdx) => (
          <React.Fragment key={dayIdx}>
            {/* Day label */}
            <div
              className="text-[10px] sm:text-xs"
              style={{
                color: colors.text.secondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: 4,
              }}
            >
              {label}
            </div>
            {/* Cells for each week */}
            {Array.from({ length: weeks }, (_, weekIdx) => {
              const idx = weekIdx * 7 + dayIdx;
              const day = days[idx];
              const intensity = getIntensity(day.count);
              const isCurrentWeek = weekIdx === currentWeekIdx;
              return (
                <div
                  key={`cell-${weekIdx}-${dayIdx}`}
                  style={{
                    backgroundColor: getCellColor(intensity),
                    borderRadius: CELL_RADIUS,
                    aspectRatio: '1',
                    width: '100%',
                    maxHeight: 28,
                    outline: isCurrentWeek
                      ? `1.5px solid color-mix(in srgb, ${colors.primary} 30%, transparent)`
                      : undefined,
                    outlineOffset: -1,
                  }}
                  title={`${day.date}: ${day.count} cards`}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {/* Legend */}
      <div
        className="flex items-center gap-1.5 mt-2 justify-end text-[10px] sm:text-xs"
        style={{ color: colors.text.secondary }}
      >
        <span>Less</span>
        {[0, 1, 2, 3, 4].map(level => (
          <div
            key={level}
            style={{
              width: 10,
              height: 10,
              borderRadius: LEGEND_DOT_RADIUS,
              backgroundColor: getCellColor(level),
            }}
          />
        ))}
        <span>More</span>
      </div>
    </div>
  );
}
